/**
 * Live monitoring — load Patrol Settings drawing + camera placements + admin cameras.
 */

import { adminNodeListCameras } from "../../../api/adminNodeCameras.ts";
import { startCameraRelay } from "../../../api/cameras.ts";
import { fetchCameraHotspots } from "../../../api/cameraHotspotsApi.ts";
import {
  fetchPatrolDrawings,
  fetchPatrolSectionCameras,
} from "../../../api/patrol.ts";
import { waitForHlsPlaylistReady } from "../../../lib/live-stream.ts";
import { patrolCameraOptionFromAdminRow } from "../../administration/patrolAdmin/patrolSectionCameraLocation.js";
import {
  markersForDisplay,
  normalizePlacements,
} from "../../administration/patrolAdmin/patrolSectionCameraLocation.js";
import { mapCameraHotspotsToPatrolAsset } from "./patrolAssetHotspots.js";
import { normalizeCameraVendor } from "../../../lib/cameraVendor.ts";

function adminCamera360Rtsp(row) {
  if (!row || typeof row !== "object") return "";
  return String(
    row.cam360_rtsp ??
      row.cam360Rtsp ??
      row.streaming_url ??
      row.stream_url ??
      "",
  ).trim();
}

function adminCameraPtzRtsp(row) {
  if (!row || typeof row !== "object") return "";
  return String(row.ptz_rtsp ?? row.ptzRtsp ?? "").trim();
}

/** @returns {"ptz"|"360"|"dual"} */
export function patrolAdminCameraStreamKind(row) {
  const has360 = Boolean(adminCamera360Rtsp(row));
  const hasPtz = Boolean(adminCameraPtzRtsp(row));
  if (hasPtz && !has360) return "ptz";
  if (has360 && hasPtz) return "dual";
  return "360";
}

function adminCameraRtspUrl(row) {
  const kind = patrolAdminCameraStreamKind(row);
  if (kind === "ptz") return adminCameraPtzRtsp(row);
  return adminCamera360Rtsp(row);
}

function adminCameraById(rows) {
  const map = new Map();
  (rows || []).forEach((row) => {
    const opt = patrolCameraOptionFromAdminRow(row);
    if (!opt?.id) return;
    const streamKind = patrolAdminCameraStreamKind(row);
    map.set(opt.id, {
      ...opt,
      rtspUrl: adminCameraRtspUrl(row),
      cam360RtspUrl: adminCamera360Rtsp(row),
      ptzRtspUrl: adminCameraPtzRtsp(row),
      streamKind,
      vendor: normalizeCameraVendor(
        row.vendor ?? row.camera_vendor ?? row.cameraVendor,
      ),
    });
  });
  return map;
}

function isHlsPlaylistUrl(url) {
  const s = String(url ?? "").trim();
  return Boolean(s && /\.m3u8(\?|$)/i.test(s));
}

/** Prefer favorite drawing, else first with a file URL. */
export function pickPatrolLiveDrawing(drawings) {
  if (!Array.isArray(drawings) || !drawings.length) return null;
  const fav = drawings.find((d) => d.isFavorite && d.fileUrl);
  if (fav) return fav;
  return drawings.find((d) => d.fileUrl) || drawings[0];
}

/**
 * Build `selectedPatrol`-shaped rows for floor plan + 360 player (one per placed camera).
 * @param {import("../../administration/patrolAdmin/patrolSectionCameraLocation.js").PatrolCameraPlacement[]} placements
 * @param {Map<string, { id: string, name: string, rtspUrl?: string }>} camerasById
 * @param {Record<string, unknown[]>} hotspotsByCameraId
 * @param {Record<string, { hls360: string | null, hlsPtz: string | null, webrtc360?: string | null, webrtcPtz?: string | null }>} hlsRelaysByCameraId
 */
export function buildLivePatrolRecordings(
  placements,
  camerasById,
  hotspotsByCameraId = {},
  hlsRelaysByCameraId = {},
) {
  return normalizePlacements(placements)
    .filter((p) => p.locations.length > 0)
    .map((p, index) => {
      const routeIndex = Number.isFinite(p.sortOrder) ? p.sortOrder : index;
      const cam = camerasById.get(p.cameraId);
      const loc = p.locations[0];
      const label = p.label || cam?.name || `Camera ${index + 1}`;
      const rtsp = cam?.rtspUrl || "";
      const relay = hlsRelaysByCameraId[p.cameraId] || {};
      const hls360 = relay.hls360 || null;
      const hlsPtz = relay.hlsPtz || null;
      const webrtc360 = relay.webrtc360 || null;
      const webrtcPtz = relay.webrtcPtz || null;
      const streamKind = cam?.streamKind || "360";
      const liveStreamKind = streamKind === "dual" ? "360" : streamKind;
      const primaryHls =
        liveStreamKind === "ptz"
          ? hlsPtz || hls360
          : hls360 || hlsPtz;
      const hotspots = hotspotsByCameraId[p.cameraId] || [];

      const cameraType =
        streamKind === "ptz"
          ? "PTZ"
          : streamKind === "dual"
            ? "360° + PTZ"
            : "360°";

      return {
        pin_id: p.cameraId,
        camera_id: p.cameraId,
        pin: [
          {
            xPercent: loc.xPercent,
            yPercent: loc.yPercent,
            duration: loc.duration || "2",
            label,
          },
        ],
        created_at: new Date(Date.now() + routeIndex).toISOString(),
        patrol_route_index: routeIndex,
        recording_path: primaryHls || "",
        live_hls_url: isHlsPlaylistUrl(primaryHls) ? primaryHls : "",
        live_hls_360_url: isHlsPlaylistUrl(hls360) ? hls360 : "",
        live_hls_ptz_url: isHlsPlaylistUrl(hlsPtz) ? hlsPtz : "",
        live_webrtc_360_url: String(webrtc360 ?? "").trim(),
        live_webrtc_ptz_url: String(webrtcPtz ?? "").trim(),
        live_stream_kind: liveStreamKind,
        camera_type: cameraType,
        camera_label: label,
        camera_vendor: normalizeCameraVendor(cam?.vendor),
        live_rtsp_url: rtsp || "",
        duration_in_seconds: 0,
        asset_hotspots: hotspots,
      };
    });
}

/**
 * @typedef {{
 *   drawing: import("../../../api/patrol.ts").PatrolDrawingUi | null,
 *   drawingImageUrl: string | null,
 *   placements: import("../../administration/patrolAdmin/patrolSectionCameraLocation.js").PatrolCameraPlacement[],
 *   floorMarkers: Array,
 *   patrolRecordings: ReturnType<typeof buildLivePatrolRecordings>,
 *   cameras: Array<{ id: string, name: string, site: string, type: string, rtspUrl?: string }>,
 * }} PatrolLiveMonitoringContext
 */

/**
 * Load drawing, placements, cameras, and hotspots for Live monitoring tab.
 * @returns {Promise<PatrolLiveMonitoringContext>}
 */
export async function loadPatrolLiveMonitoringContext() {
  const empty = {
    drawing: null,
    drawingImageUrl: null,
    placements: [],
    floorMarkers: [],
    patrolRecordings: [],
    cameras: [],
  };

  const [drawings, cameraRows] = await Promise.all([
    fetchPatrolDrawings().catch((err) => {
      console.warn("Patrol drawings unavailable for live monitoring.", err);
      return [];
    }),
    adminNodeListCameras().catch((err) => {
      console.warn("Camera list unavailable for live monitoring.", err);
      return [];
    }),
  ]);

  const drawing = pickPatrolLiveDrawing(drawings);
  if (!drawing?.id) {
    return { ...empty, cameras: Array.from(adminCameraById(cameraRows).values()) };
  }

  const placements = await fetchPatrolSectionCameras(drawing.id).catch((err) => {
    console.warn("Patrol section cameras unavailable.", err);
    return normalizePlacements(drawing.cameraPlacements);
  });

  const camerasById = adminCameraById(cameraRows);
  const cameraOptions = Array.from(camerasById.values());

  const placedIds = [
    ...new Set(
      normalizePlacements(placements)
        .map((p) => p.cameraId)
        .filter(Boolean),
    ),
  ];

  const hotspotsByCameraId = {};
  const hlsRelaysByCameraId = {};
  await Promise.all(
    placedIds.map(async (cameraId) => {
      try {
        const list = await fetchCameraHotspots(cameraId);
        hotspotsByCameraId[cameraId] = mapCameraHotspotsToPatrolAsset(list);
      } catch (err) {
        console.warn(`Hotspots unavailable for camera ${cameraId}`, err);
        hotspotsByCameraId[cameraId] = [];
      }
      try {
        const relay = await startCameraRelay(cameraId);
        let hls360 = relay.hls360 || null;
        let hlsPtz = relay.hlsPtz || null;
        if (hls360) {
          const ready = await waitForHlsPlaylistReady(hls360, 30_000);
          if (!ready) {
            console.warn(`360° HLS not ready for camera ${cameraId}`);
            hls360 = null;
          }
        }
        if (hlsPtz) {
          const ready = await waitForHlsPlaylistReady(hlsPtz, 30_000);
          if (!ready) {
            console.warn(`PTZ HLS not ready for camera ${cameraId}`);
            hlsPtz = null;
          }
        }
        hlsRelaysByCameraId[cameraId] = {
          hls360,
          hlsPtz,
          webrtc360: relay.webrtc360 || null,
          webrtcPtz: relay.webrtcPtz || null,
        };
      } catch (err) {
        console.warn(`Camera relay unavailable for ${cameraId}`, err);
        hlsRelaysByCameraId[cameraId] = {
          hls360: null,
          hlsPtz: null,
          webrtc360: null,
          webrtcPtz: null,
        };
      }
    }),
  );

  const patrolRecordings = buildLivePatrolRecordings(
    placements,
    camerasById,
    hotspotsByCameraId,
    hlsRelaysByCameraId,
  );

  const floorMarkers = markersForDisplay(placements, cameraOptions);

  return {
    drawing,
    drawingImageUrl: drawing.fileUrl || drawing.imagePreview || null,
    placements: normalizePlacements(placements),
    floorMarkers,
    patrolRecordings,
    cameras: cameraOptions,
  };
}
